import React, { useState, useEffect, memo } from "react";
import {
  Typography,
  Alert,
  IconButton,
  CardContent,
  Tooltip,
  DialogTitle,
  DialogContent,
  Card,
  Divider,
} from "@mhome/ui";
import CircularProgress from "@mui/material/CircularProgress";
import { useMediaQuery } from "@mui/material";
import {
  AutoAwesome,
  Error as ErrorIcon,
  PlayCircleFilledWhite as PlayCircleFilledWhiteIcon,
  Token as TokenIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { useRecoilValue } from "recoil";
import { wsReadyState, safeAreaInsetsState } from "../global-state";
import {
  getEntityAutomationRelations,
  getAutomation,
  enableAutomation,
  disableAutomation,
  runningAutomation,
} from "../service/automation-service";
import { batchGet } from "../service/inspire-service";
import AutomationEditCardContent from "./automation-edit-card-content";
import { useWebSocket } from "../ws/websocket";
import { useComponentBackgroundColor } from "../common/adaptive-theme-provider";
import { useEventEmitter } from "../ws/event-context";
import { useToast } from "../common/toast";
import { getTagIcon } from "../common/tag";
import MSwitch from "../common/switch";
import ResponsiveCardGrid from "../common/responsive-card-grid";
import dayjs from "dayjs";
import { useCustomFormat } from "../views/after-init";
import MDialog from "../common/m-dialog";
import AutomationWidget from "../common/automation-widget";
import { getAutomationInfo } from "../service/automation-service";

const EntityAutomation = memo(
  ({ automationFetch, inspireFetch }) => {
    const [error, setError] = useState(null);
    const [relations, setRelations] = useState([]);
    const [automationData, setAutomationData] = useState({});
    const [inspireData, setInspireData] = useState([]);
    const [inspireArgsMap, setInspireArgsMap] = useState({});
    const [enablingDisablingId, setEnablingDisablingId] = useState(null);
    const [currentTime, setCurrentTime] = useState(dayjs());
    const [selectedAutomation, setSelectedAutomation] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedAutomationWidgets, setSelectedAutomationWidgets] = useState(
      []
    );
    const wsReady = useRecoilValue(wsReadyState);
    const safeAreaInsets = useRecoilValue(safeAreaInsetsState);
    const isSmallScreen = useMediaQuery("@media (max-width:959px)");
    const { sendAndWaitWs } = useWebSocket();
    const { subscribe } = useEventEmitter();
    const { successToast, errorToast } = useToast();
    const customFormat = useCustomFormat();
    const getComponentBgColor = useComponentBackgroundColor();

    useEffect(() => {
      const intervalId = setInterval(() => {
        setCurrentTime(dayjs());
      }, 60000);

      return () => clearInterval(intervalId);
    }, []);

    useEffect(() => {
      const { unsubscribe } = subscribe((target, data) => {
        if (target === "/automation/history/reportUpdate") {
          const payload = JSON.parse(data.payload);
          if (payload) {
            updateAutomation(payload);
          }
        } else if (target === "/automation/runningStatus/reportUpdate") {
          const payload = JSON.parse(data.payload);
          if (payload) {
            updateAutomation(payload);
          }
        }
      });
      return () => {
        unsubscribe();
      };
    }, []);

    useEffect(() => {
      const fetchData = async () => {
        const promises = [];
        if (automationFetch) {
          promises.push(fetchEntityAutomations());
        }
        if (inspireFetch) {
          promises.push(fetchInspireAutomations());
        }
        if (promises.length > 0) {
          await Promise.all(promises);
        }
      };
      if (wsReady) {
        fetchData();
      }
    }, [automationFetch, inspireFetch, wsReady]);

    const fetchEntityAutomations = async () => {
      try {
        setError(null);

        // Step 1: Get entity automation relations (automation IDs)
        const relationsResponse = await getEntityAutomationRelations(
          sendAndWaitWs,
          automationFetch.entityType,
          automationFetch.integration,
          automationFetch.entityId,
          automationFetch.filter || {}
        );

        if (!relationsResponse || relationsResponse.error) {
          throw new Error(
            relationsResponse?.error ||
              "Failed to fetch entity automation relations"
          );
        }

        const relations = relationsResponse.relations || [];
        setRelations(relations);

        if (relations.length === 0) {
          return;
        }

        // Step 2: Get automation IDs from relations
        const automationIds = relations.map(
          (relation) => relation.automationId
        );

        // Step 3: Get automation data for each automation ID
        const automationMap = {};
        await Promise.all(
          automationIds.map(async (automationId) => {
            try {
              const automationInfo = await getAutomation(
                sendAndWaitWs,
                automationId
              );
              if (automationInfo && !automationInfo.error) {
                automationMap[automationId] = automationInfo;
              }
            } catch (err) {
              console.error(
                `Error fetching automation info for ${automationId}:`,
                err
              );
            }
          })
        );
        setAutomationData(automationMap);
      } catch (err) {
        console.error("Error fetching entity automations:", err);
        setError(err.message || "Failed to load automations");
      }
    };

    const fetchInspireAutomations = async (setLoading = false) => {
      try {
        setError(null);
        console.log("fetchInspireAutomations", inspireFetch);
        if (!inspireFetch) {
          setInspireData([]);
          setInspireArgsMap({});
          return;
        }

        const ids = inspireFetch.map((item) => item.id);
        const argsMap = {};
        inspireFetch.forEach((item) => {
          argsMap[item.id] = item.args;
        });
        setInspireArgsMap(argsMap);

        // Call batchGet with the extracted ids
        const response = await batchGet(sendAndWaitWs, ids);
        console.log("response", response);
        if (!response || response.error) {
          throw new Error(
            response?.error || "Failed to fetch inspire automations"
          );
        }
        const inspires = response.inspires || [];
        setInspireData(inspires);
      } catch (err) {
        console.error("Error fetching inspire automations:", err);
        setError(err.message || "Failed to load inspire automations");
      }
    };

    const updateAutomation = (payload) => {
      setAutomationData((prevData) => {
        const automationId = payload.automationId;
        if (prevData[automationId]) {
          return {
            ...prevData,
            [automationId]: {
              ...prevData[automationId],
              timestamp: payload.timestamp,
              running: payload.running || false,
            },
          };
        }
        return prevData;
      });
    };

    const handleEnableDisable = async (event, enableDisable, automationId) => {
      event.stopPropagation();
      setEnablingDisablingId(automationId);
      if (enableDisable) {
        const response = await enableAutomation(sendAndWaitWs, automationId);
        if (response && !response.error) {
          successToast("Enabled");
          setAutomationData((prevData) => ({
            ...prevData,
            [automationId]: {
              ...prevData[automationId],
              enabled: response.enabled,
              corrupted: response.corrupted || prevData[automationId].corrupted,
            },
          }));
        } else {
          errorToast("Error!");
        }
      } else {
        const response = await disableAutomation(sendAndWaitWs, automationId);
        if (response && !response.error) {
          successToast("Disabled");
          setAutomationData((prevData) => ({
            ...prevData,
            [automationId]: {
              ...prevData[automationId],
              enabled: response.enabled,
              corrupted: response.corrupted || prevData[automationId].corrupted,
            },
          }));
        } else {
          errorToast("Error!");
        }
      }
      setEnablingDisablingId(null);
    };

    const handleStartTask = async (event, automationId) => {
      event.stopPropagation();
      const success = await runningAutomation(sendAndWaitWs, automationId);
      if (!success) {
        errorToast("Error starting the task");
      }
    };

    const handleCardClick = async (automationId, isInspire = false) => {
      setSelectedAutomation({ automationId, isInspire });
      setDialogOpen(true);

      // Fetch automation info to get widgets if it's not an inspire
      if (!isInspire) {
        try {
          const info = await getAutomationInfo(sendAndWaitWs, automationId);
          if (
            info &&
            !info.error &&
            info.widgets &&
            Array.isArray(info.widgets)
          ) {
            setSelectedAutomationWidgets(info.widgets);
          } else {
            setSelectedAutomationWidgets([]);
          }
        } catch (error) {
          console.error("Error fetching automation info:", error);
          setSelectedAutomationWidgets([]);
        }
      } else {
        setSelectedAutomationWidgets([]);
      }
    };

    const handleCloseDialog = () => {
      setDialogOpen(false);
      setSelectedAutomation(null);
      setSelectedAutomationWidgets([]);
    };

    const renderAutomationCard = (relation) => {
      const automation = automationData[relation.automationId];
      if (!automation) return null;

      const isRecurring = automation.type === "RECURRING";
      const isTaskFlow = automation.type === "TASK_FLOW";

      return (
        <Card
          key={relation.automationId}
          onClick={() => handleCardClick(relation.automationId, false)}
          elevation={0}
          className="cursor-pointer transition-all duration-200"
          bgcolor={getComponentBgColor("hsl(var(--card))")}
          style={{
            WebkitTapHighlightColor: "transparent",
            border: `1px solid ${"hsl(var(--border))"}`,
          }}
          onMouseDown={(e) => {
            e.currentTarget.style.backgroundColor = "hsl(var(--accent) / 0.5)";
          }}
          onMouseUp={(e) => {
            e.currentTarget.style.backgroundColor =
              getComponentBgColor("hsl(var(--card))");
          }}
        >
          <CardContent className="p-2 flex flex-col" style={{ minHeight: 0 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "8px",
                minHeight: "24px",
                flexShrink: 0,
              }}
            >
              {/* Tag Icons */}
              <div
                style={{
                  display: "flex",
                  gap: "4px",
                  alignItems: "center",
                  overflow: "hidden",
                  flex: 1,
                  marginRight: "8px",
                }}
              >
                {automation.tags &&
                  automation.tags.length > 0 &&
                  automation.tags.map((tag, tagIndex) => (
                    <div
                      key={tagIndex}
                      style={{
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      {getTagIcon(tag, 16)}
                    </div>
                  ))}
              </div>

              {/* Control buttons */}
              {automation.blocked ? (
                <Tooltip title="Insufficient tokens - subscription ended or used up">
                  <TokenIcon
                    style={{
                      color: "hsl(var(--destructive))",
                      marginRight: isRecurring ? "15px" : "0px",
                      marginLeft: isRecurring ? "15px" : "0px",
                    }}
                  />
                </Tooltip>
              ) : !automation.runnable ? (
                <ErrorIcon
                  style={{
                    color: "red",
                    marginRight: isRecurring ? "15px" : "0px",
                    marginLeft: isRecurring ? "15px" : "0px",
                  }}
                />
              ) : isRecurring ? (
                enablingDisablingId === automation.id ? (
                  <CircularProgress size={24} />
                ) : (
                  <MSwitch
                    checked={automation.enabled}
                    disabled={!automation.runnable}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e, b) =>
                      handleEnableDisable(e, b, automation.id)
                    }
                    inputProps={{ "aria-label": "controlled" }}
                  />
                )
              ) : isTaskFlow ? (
                automation.running ? (
                  <CircularProgress size={24} />
                ) : (
                  <PlayCircleFilledWhiteIcon
                    onClick={(e) => handleStartTask(e, automation.id)}
                    style={{
                      cursor: "pointer",
                      color: "hsl(var(--primary))",
                    }}
                  />
                )
              ) : null}
            </div>

            {/* Automation Name - 2 lines max */}
            <Typography
              variant="body2"
              component="div"
              className="mb-1"
              style={{
                display: "-webkit-box",
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: 2,
                overflow: "hidden",
                textOverflow: "ellipsis",
                lineHeight: "1.5em",
                minHeight: "3em",
                maxHeight: "3em",
                color: "hsl(var(--foreground))",
                fontSize: "0.875rem",
                fontWeight: 500,
                flexShrink: 0,
              }}
            >
              {automation.name}
            </Typography>

            {/* Automation ID - 1 line only */}
            <div
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                color: "hsl(var(--muted-foreground))",
                fontSize: "0.75rem",
                lineHeight: "1.2em",
                width: "100%",
                marginTop: "4px",
                flexShrink: 0,
              }}
            >
              {relation.automationId}
            </div>
          </CardContent>
        </Card>
      );
    };

    const handleInspireSave = async (automationId, inspireId) => {
      if (automationFetch && automationId) {
        await fetchEntityAutomations();
      }

      // Remove the saved inspire from the inspire data to hide its card
      if (inspireId) {
        setInspireData((prevData) =>
          prevData.filter((inspire) => inspire.id !== inspireId)
        );
        setInspireArgsMap((prevMap) => {
          const newMap = { ...prevMap };
          delete newMap[inspireId];
          return newMap;
        });
      }
    };

    const renderInspireCard = (inspire) => {
      return (
        <Card
          key={inspire.id}
          onClick={() => handleCardClick(inspire.id, true)}
          elevation={0}
          className="cursor-pointer transition-all duration-200"
          bgcolor={getComponentBgColor("hsl(var(--card))")}
          style={{
            WebkitTapHighlightColor: "transparent",
            border: `1px solid ${"hsl(var(--border))"}`,
          }}
          onMouseDown={(e) => {
            e.currentTarget.style.backgroundColor = "hsl(var(--accent) / 0.5)";
          }}
          onMouseUp={(e) => {
            e.currentTarget.style.backgroundColor =
              getComponentBgColor("hsl(var(--card))");
          }}
        >
          <CardContent className="p-2 h-full flex flex-col">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "8px",
                minHeight: "24px",
              }}
            >
              {/* Tag Icons */}
              <div
                style={{
                  display: "flex",
                  gap: "4px",
                  alignItems: "center",
                  overflow: "hidden",
                  flex: 1,
                  marginRight: "8px",
                }}
              >
                {inspire.tags &&
                  inspire.tags.length > 0 &&
                  inspire.tags.map((tag, tagIndex) => (
                    <div
                      key={tagIndex}
                      style={{
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      {getTagIcon(tag, 16)}
                    </div>
                  ))}
              </div>

              {/* Sparkle icon for inspire */}
              <AutoAwesome
                style={{
                  color: "hsl(var(--primary))",
                  fontSize: 20,
                }}
              />
            </div>

            {/* Inspire Name */}
            <Typography
              gutterBottom
              variant="body1"
              component="div"
              className="mb-0"
              style={{
                display: "-webkit-box",
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: 3,
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxHeight: "4.5em",
              }}
            >
              {inspire.name}
            </Typography>

            {/* Description */}
            {inspire.description && (
              <Typography
                variant="caption"
                component="div"
                className="mt-1.5"
                style={{
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: 2,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  color: "hsl(var(--muted-foreground))",
                }}
              >
                {inspire.description}
              </Typography>
            )}

            {/* Creator */}
            {inspire.creator && (
              <Typography
                variant="caption"
                component="div"
                className="mt-1.5"
                style={{
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  color: "hsl(var(--muted-foreground))",
                }}
              >
                {`By ${inspire.creator}`}
              </Typography>
            )}
          </CardContent>
        </Card>
      );
    };

    if (error) {
      return (
        <Alert severity="error" className="mb-4">
          {error}
        </Alert>
      );
    }

    // Determine what to render based on fetch type
    const hasEntityAutomations = automationFetch && relations.length > 0;
    const hasInspireAutomations = inspireFetch && inspireData.length > 0;
    const showEmptyAutomations = automationFetch && relations.length === 0;
    const showEmptyInspires = inspireFetch && inspireData.length === 0;

    if (
      !hasEntityAutomations &&
      !hasInspireAutomations &&
      !showEmptyAutomations &&
      !showEmptyInspires
    ) {
      return null;
    }

    return (
      <>
        <div style={{ width: "100%" }}>
          {/* Automations Section */}
          {automationFetch && (
            <>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "16px",
                }}
              >
                <Typography
                  variant="h6"
                  color="textPrimary"
                  className="flex-grow"
                  style={{
                    fontWeight: 500,
                  }}
                >
                  Automations
                </Typography>
              </div>
              {hasEntityAutomations ? (
                <ResponsiveCardGrid
                  spacing={{ xs: 1.5, sm: 2 }}
                  breakpoints={{ xs: 1, sm: 2 }}
                >
                  {relations.map(renderAutomationCard)}
                </ResponsiveCardGrid>
              ) : (
                <Typography
                  variant="body2"
                  color="textSecondary"
                  className="mb-4"
                >
                  No related automations found
                </Typography>
              )}
            </>
          )}

          {/* Divider between Automations and Inspirations */}
          {automationFetch &&
            inspireFetch &&
            hasEntityAutomations &&
            hasInspireAutomations && (
              <Divider style={{ marginTop: "24px", marginBottom: "24px" }} />
            )}

          {/* Inspire/Inspirations Section */}
          {inspireFetch && (
            <>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "16px",
                  marginTop: automationFetch ? "24px" : 0,
                }}
              >
                <Typography
                  variant="h6"
                  color="textPrimary"
                  className="flex-grow"
                  style={{
                    fontWeight: 500,
                  }}
                >
                  Inspirations
                </Typography>
              </div>
              {hasInspireAutomations ? (
                <ResponsiveCardGrid
                  spacing={{ xs: 1.5, sm: 2 }}
                  breakpoints={{ xs: 1, sm: 2 }}
                >
                  {inspireData.map(renderInspireCard)}
                </ResponsiveCardGrid>
              ) : (
                <Typography
                  variant="body2"
                  color="textSecondary"
                  className="mb-4"
                >
                  No inspirations available
                </Typography>
              )}
            </>
          )}
        </div>

        {/* Dialog for editing automation */}
        {selectedAutomation && (
          <MDialog
            hash={null}
            open={dialogOpen}
            fullScreen={isSmallScreen}
            variant={isSmallScreen ? undefined : "drawer"}
            onClose={handleCloseDialog}
            PaperProps={{
              style: {
                width: isSmallScreen ? "100%" : "600px",
                maxWidth: isSmallScreen ? "100%" : "600px",
                minWidth: isSmallScreen ? "100%" : "600px",
                marginTop: isSmallScreen ? 0 : `${safeAreaInsets.top}px`,
                marginBottom: isSmallScreen ? 0 : `${safeAreaInsets.bottom}px`,
                height: isSmallScreen
                  ? "100vh"
                  : `calc(100vh - ${
                      safeAreaInsets.top + safeAreaInsets.bottom
                    }px) !important`,
                maxHeight: isSmallScreen
                  ? "100vh"
                  : `calc(100vh - ${
                      safeAreaInsets.top + safeAreaInsets.bottom
                    }px) !important`,
                paddingTop: isSmallScreen ? `${safeAreaInsets.top}px` : 0,
                paddingBottom: isSmallScreen ? `${safeAreaInsets.bottom}px` : 0,
                paddingRight: `${safeAreaInsets.right}px`,
              },
            }}
          >
            <DialogTitle className="gap-4 flex items-start min-h-0">
              <IconButton
                aria-label="close"
                onClick={handleCloseDialog}
                className="flex-shrink-0"
              >
                <CloseIcon />
              </IconButton>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="h6"
                  component="div"
                  className="pt-3 mb-0"
                  style={{
                    lineHeight: 1.2,
                  }}
                >
                  {selectedAutomation.isInspire
                    ? inspireData.find(
                        (p) => p.id === selectedAutomation.automationId
                      )?.name || "Automation"
                    : automationData[selectedAutomation.automationId]?.name ||
                      "Automation"}
                </Typography>
              </div>
            </DialogTitle>

            <DialogContent className="p-4 overflow-auto">
              <AutomationEditCardContent
                params={
                  selectedAutomation.isInspire
                    ? {
                        inspireId: selectedAutomation.automationId,
                        inspireVersion: inspireData.find(
                          (p) => p.id === selectedAutomation.automationId
                        )?.version,
                        args:
                          inspireArgsMap[selectedAutomation.automationId] || {},
                      }
                    : {
                        automationId: selectedAutomation.automationId,
                      }
                }
                editable={true}
                onSave={async (automationId) => {
                  if (selectedAutomation.isInspire) {
                    await handleInspireSave(
                      automationId,
                      selectedAutomation.automationId
                    );
                  } else {
                    await fetchEntityAutomations();
                  }
                  handleCloseDialog();
                }}
                onDelete={() => {
                  handleCloseDialog();
                }}
                onEnableDisable={() => {}}
              />
              {/* Render widgets if available */}
              {!selectedAutomation.isInspire &&
                selectedAutomationWidgets.length > 0 && (
                  <AutomationWidget widgets={selectedAutomationWidgets} />
                )}
            </DialogContent>
          </MDialog>
        )}
      </>
    );
  },
  (prevProps, nextProps) => {
    if (prevProps.automationFetch !== nextProps.automationFetch) {
      if (!prevProps.automationFetch || !nextProps.automationFetch) {
        return false;
      }
      if (
        prevProps.automationFetch.entityType !==
          nextProps.automationFetch.entityType ||
        prevProps.automationFetch.entityId !==
          nextProps.automationFetch.entityId ||
        JSON.stringify(prevProps.automationFetch.filter) !==
          JSON.stringify(nextProps.automationFetch.filter)
      ) {
        return false;
      }
    }
    if (prevProps.inspireFetch !== nextProps.inspireFetch) {
      if (!prevProps.inspireFetch || !nextProps.inspireFetch) {
        return false;
      }
      if (
        JSON.stringify(prevProps.inspireFetch) !==
        JSON.stringify(nextProps.inspireFetch)
      ) {
        return false;
      }
    }
    return true;
  }
);

EntityAutomation.displayName = "EntityAutomation";

export default EntityAutomation;
